import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BASE_URL_TOKEN, InspectionTemplate, InspectionTemplateScope, InspectionTemplateUsage, SearchCriteria } from '@raptor-fe/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class InspectionTemplateService {
    private http = inject(HttpClient);
    private baseUrl = `${inject(BASE_URL_TOKEN)}v1/tenant/`;

    private httpHeadersWithNoLoading(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'No-Loading': 'true',
        });
    }

    clone(
        tenantId: string,
        branchId: string,
        templateId: string,
        targetBranchId: string,
        targetScope?: InspectionTemplateScope,
    ): Observable<InspectionTemplate> {
        let params = new HttpParams().set('targetBranchId', targetBranchId);

        if (targetScope !== undefined) {
            params = params.set('targetScope', targetScope);
        }

        return this.http.post<InspectionTemplate>(
            `${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/${templateId}/clone`,
            {},
            { params },
        );
    }

    delete(tenantId: string, branchId: string, id: string): Observable<object> {
        return this.http.delete<object>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/${id}`);
    }

    getAll(tenantId: string, branchId: string): Observable<InspectionTemplate[]> {
        return this.http.get<InspectionTemplate[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate`);
    }

    get(tenantId: string, branchId: string, id: string): Observable<InspectionTemplate | null> {
        if (id === null || id === undefined || id === '')
            return of({
                tenantId: tenantId,
                branchId: branchId,
                id: id,
                sections: [],
                usage: InspectionTemplateUsage.Standard,
            } as InspectionTemplate);
        else return this.http.get<InspectionTemplate>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/${id}`);
    }

    getItemSuggestions(
        tenantId: string,
        branchId: string,
        templateName: string,
        sectionName: string,
        pageSize = 10,
        keyword?: string | null,
    ): Observable<string[]> {
        const body = { keyword, pageSize, templateName, sectionName };

        return this.http.post<string[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/item-suggestions`, body, {
            headers: this.httpHeadersWithNoLoading(),
        });
    }

    getNameSuggestions(tenantId: string, branchId: string, pageSize = 10, keyword?: string | null): Observable<string[]> {
        const body = { keyword, pageSize };

        return this.http.post<string[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/name-suggestions`, body, {
            headers: this.httpHeadersWithNoLoading(),
        });
    }

    getSectionSuggestions(tenantId: string, branchId: string, templateName: string, pageSize = 10, keyword?: string | null): Observable<string[]> {
        const body = { keyword, pageSize, templateName };

        return this.http.post<string[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/section-suggestions`, body, {
            headers: this.httpHeadersWithNoLoading(),
        });
    }

    /**
     * Creates (POST) or updates (PUT) an inspection template.
     *
     * The route is built from the caller's context tenant and branch, never from `template.branchId`.
     * A global template keeps the branch that created it as `branchId`, and permission claims are
     * scoped to the user's current branch, so routing by the owner branch would be authorized against
     * a branch the user may not hold permissions in. The backend resolves the stored owner branch itself.
     *
     * @param tenantId - The context tenant ID
     * @param branchId - The context branch ID the user is working in
     * @param template - The template to create or update
     */
    save(tenantId: string, branchId: string, template: InspectionTemplate): Observable<InspectionTemplate> {
        const url = `${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate`;

        if (template.id === null || template.id === undefined || template.id === '') {
            return this.http.post<InspectionTemplate>(url, template);
        } else {
            return this.http.put<InspectionTemplate>(url, template);
        }
    }

    search(tenantId: string, branchId: string, criteria: SearchCriteria, showLoading = true): Observable<InspectionTemplate[]> {
        return this.http.post<InspectionTemplate[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/search`, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading(),
        });
    }

    /**
     * Gets all CheckIn inspection templates for the given tenant and branch.
     * Used during check-in flow to display template selection to advisor.
     * @param tenantId - The tenant ID
     * @param branchId - The branch ID
     * @returns Observable of inspection template array
     */
    getCheckInTemplates(tenantId: string, branchId: string): Observable<InspectionTemplate[]> {
        return this.http.get<InspectionTemplate[]>(`${this.baseUrl}${tenantId}/branch/${branchId}/inspectionTemplate/checkin-templates`);
    }
}
